import { AxiosError } from 'axios';
import { authHeader, authUploadService } from 'services';
import {
  ICompany,
  ICompanyCreate,
  ICompanyFile,
  ICompanyList,
} from 'types/Company';
import { getUserToken } from 'util/auth';

import { Api } from './api/axios.service';

const FILE_DOWNLOAD_URL = `${process.env.REACT_APP_BASE_API_URL}/api/company`;

/**
 * List companies
 *
 * @param {number} page
 * @param {number} amount
 * @return {*}  {Promise<ICompanyList>}
 */
const getCompaniesList = async (
  page: number,
  amount: number
): Promise<ICompanyList> => {
  try {
    const { data } = await Api.get<ICompanyList>(
      `/company?page=${page}&amount=${amount}&uf=&filter=&status=all`,
      {
        headers: authHeader(),
      }
    );

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível listar as empresas'
    );
  }
};

/**
 * List company's documents
 *
 * @param {(string | undefined)} idCompany
 * @param {string} documentId
 * @return {*}
 */
const getCompanyDocuments = (idCompany?: string, documentId?: string) => {
  return `${FILE_DOWNLOAD_URL}/downloadDocument/${idCompany}/?access_token=Bearer ${getUserToken().token
    }&documentId=${documentId}`;
};

/**
 * Register or update a company from admin
 *
 * @param {ICompanyCreate} company
 * @return {*}  {Promise<void>}
 */
const createOrUpdateCompany = async (
  company: ICompanyCreate
): Promise<void> => {
  const formData = new FormData();
  
  if (!company.cnpj) {
    company.cnpj = null;
  }

  Object.entries(company).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (key === 'documents' && Array.isArray(value)) {
      value.filter((doc: ICompanyFile) =>
        Boolean(doc.file)
      ).forEach((document: ICompanyFile) => {
        formData.append('documentsFiles', document.file as File);
      });

      const oldDocuments = value.filter((doc: ICompanyFile) =>
        !doc.file
      )

      return formData.append('documents', JSON.stringify(oldDocuments));
    }

    if (!(value instanceof File) && typeof value === 'object') {
      return formData.append(key, JSON.stringify(value));
    }

    formData.append(key, value);
  });

  try {
    /* If has id should update */
    if (company._id) {
      await Api.put(`/company/${company._id}`, formData, {
        headers: authUploadService(),
      });

      return;
    }

    await Api.post('/company', formData, {
      headers: authUploadService(),
    });
  } catch (reason) {
    const error = (reason as AxiosError<{ error: string }>).response?.data
      ?.error;

    let message = error;

    if (typeof error !== 'string') {
      if (company._id) {
        message = 'Não foi possível atualizar a empresa';
      } else {
        message = 'Não foi possível cadastrar a empresa';
      }
    }

    throw new Error(message);
  }
};

/**
 * Find company by id
 *
 * @param {{ id: string }} { id }
 * @return {*}  {Promise<ICompany>}
 */
const findCompanyById = async ({ id }: { id: string }): Promise<ICompany> => {
  try {
    const { data } = await Api.get<ICompany>(`/company/findById/${id}`, {
      headers: authUploadService(),
    });
    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível buscar a empresa'
    );
  }
};

/* Delete company by id
 *
 * @param {(string | undefined)} companyId
 * @return {*}
 */
const deleteCompanyById = async (companyId?: string): Promise<any> => {
  try {
    await Api.delete(`/company/${companyId}`, {
      headers: authHeader(),
    });
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível deletar a empresa'
    );
  }
};

export const CompanyService = {
  getCompaniesList,
  createOrUpdateCompany,
  findCompanyById,
  deleteCompanyById,
  getCompanyDocuments,
};
